/**
 * localStorage persistence for the most recent calculation (US-04) — a single key,
 * mirroring the existing `CookieConsent.tsx` single-key pattern. Body metrics never
 * leave the browser (SAD §10 QG-3): no network call, telemetry or "share" path here.
 * A stale calculator version is never restored (AC-09).
 */
import { CALCULATOR_VERSION } from '../constants'
import type { CalculationResult, RawCalculatorInput } from './formula'

const STORAGE_KEY = 'calculator_saved_calculation'

export interface SavedCalculation {
  inputs: RawCalculatorInput
  result: CalculationResult
  calculatorVersion: number
}

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

/** Writes the payload stamped with the current CALCULATOR_VERSION. */
export function saveCalculation(inputs: RawCalculatorInput, result: CalculationResult): void {
  if (!isBrowser()) return
  const payload: SavedCalculation = {
    inputs,
    result,
    calculatorVersion: CALCULATOR_VERSION,
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* storage full / disabled — a saved calculation is a convenience, not a requirement */
  }
}

/**
 * Returns the saved calculation only when it parses AND its stored version matches
 * the current one. A missing, malformed or stale entry returns null without throwing
 * (AC-09).
 */
export function loadCalculation(): SavedCalculation | null {
  if (!isBrowser()) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<SavedCalculation>
    if (
      !parsed ||
      parsed.calculatorVersion !== CALCULATOR_VERSION ||
      typeof parsed.inputs !== 'object' ||
      typeof parsed.result !== 'object'
    ) {
      return null
    }
    return parsed as SavedCalculation
  } catch {
    return null
  }
}

/** Removes the saved calculation key. */
export function clearCalculation(): void {
  if (!isBrowser()) return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}
